/**
 * Account-level Google Tag Manager connection panel.
 *
 * Lives on the dedicated Tag Manager page (sidebar → Google Tag
 * Manager, see client/src/pages/TagManager.tsx) and lets the operator
 * connect their Google account ONCE — every page they own can then
 * point at any GTM container that account has edit access to. Was
 * previously embedded under Meta CAPI; split out so GTM users (Google
 * stack) don't have to dig through a Meta-branded page to find it.
 *
 * UX rationale:
 *   • Connect button is the ONLY thing visible when not connected.
 *     No container picker until a Google account exists.
 *   • Connected state shows "as user@example.com" so the operator
 *     can verify they connected the right Google identity (people
 *     commonly have personal + work Google accounts signed in).
 *   • Disconnect is destructive — confirm before clearing the token.
 *   • If GOOGLE_OAUTH_* env vars aren't set on the server, surface a
 *     clear "Operator hasn't enabled this yet" panel rather than a
 *     Connect button that fails 503 on click.
 *
 * Auth pattern: same `Bearer ${getAuthToken()}` calls as
 * AccountCapiTokens.tsx — explicit, no shared Axios layer.
 */

import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Plug,
  Unplug,
  ExternalLink,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

const API_URL = 'https://api.hitzlink.com/api';

function getAuthToken(): string {
  const fromStore = useAuthStore.getState().token;
  if (fromStore) return fromStore;
  return localStorage.getItem('token') || '';
}

interface GtmStatus {
  configured: boolean;
  oauth: {
    connected: boolean;
    email: string | null;
    scope: string | null;
    connectedAt: string | null;
    lastUsedAt: string | null;
  };
}

export const GtmConnectPanel: React.FC = () => {
  const { t } = useTranslation('tracking');

  const [status, setStatus] = React.useState<GtmStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  // Read OAuth callback query params on mount so we can show a toast
  // when the operator returns from Google. The route handler appends
  // ?gtm=connected or ?gtm=error&reason=... — we strip them after
  // reading so a refresh doesn't re-fire the toast.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gtm = params.get('gtm');
    if (!gtm) return;
    if (gtm === 'connected') {
      const email = params.get('email');
      toast.success(
        email
          ? t('gtmConnect.toastConnectedAs', { email })
          : t('gtmConnect.toastConnected')
      );
    } else if (gtm === 'error') {
      const reason = params.get('reason') || 'unknown';
      toast.error(t('gtmConnect.toastError', { reason }));
    }
    params.delete('gtm');
    params.delete('email');
    params.delete('reason');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [t]);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/gtm/status`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!r.ok) throw new Error('failed');
      const data = await r.json();
      setStatus(data);
    } catch {
      // Don't toast on initial load failure — likely a transient
      // backend reload during dev. The "configured: false" state
      // covers the operator-misconfigured case.
      setStatus({
        configured: false,
        oauth: {
          connected: false,
          email: null,
          scope: null,
          connectedAt: null,
          lastUsedAt: null,
        },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/gtm/auth-url`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await r.json();
      if (!r.ok || !data.url) {
        throw new Error(data.error || 'No URL returned');
      }
      // Top-level navigation — Google's consent screen breaks inside
      // popups (sandboxed iframes, third-party cookie restrictions,
      // mobile Safari). Top-level redirect is the canonical OAuth UX.
      window.location.href = data.url;
    } catch (e) {
      toast.error((e as Error).message);
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('gtmConnect.confirmDisconnect'))) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/gtm/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || 'Disconnect failed');
      }
      toast.success(t('gtmConnect.toastDisconnected'));
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('gtmConnect.loading')}
      </div>
    );
  }

  if (!status) return null;

  // Server hasn't been configured with Google OAuth client credentials.
  // Render a clear "you're done here" panel instead of a broken Connect
  // button that 503s on click.
  if (!status.configured) {
    return (
      <Panel>
        <Header
          icon={<AlertCircle className="w-5 h-5 text-amber-600" />}
          title={t('gtmConnect.notConfiguredTitle')}
          subtitle={t('gtmConnect.notConfiguredBody')}
        />
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
          {t('gtmConnect.notConfiguredHint')}
        </div>
      </Panel>
    );
  }

  const connected = status.oauth.connected;

  return (
    <Panel>
      <Header
        icon={
          connected ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          ) : (
            <Plug className="w-5 h-5 text-blue-600" />
          )
        }
        title={t('gtmConnect.title')}
        subtitle={t('gtmConnect.subtitle')}
      />

      {!connected && (
        <div className="mt-4 space-y-4">
          <ul className="text-sm text-gray-700 space-y-1.5 list-disc list-inside">
            <li>{t('gtmConnect.benefit1')}</li>
            <li>{t('gtmConnect.benefit2')}</li>
            <li>{t('gtmConnect.benefit3')}</li>
          </ul>
          <button
            type="button"
            onClick={handleConnect}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plug className="w-4 h-4" />
            )}
            {t('gtmConnect.connectButton')}
          </button>
          <p className="text-xs text-gray-500">
            {t('gtmConnect.scopeNotice')}
          </p>
        </div>
      )}

      {connected && (
        <div className="mt-4 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-emerald-900">
                  {t('gtmConnect.connected')}
                </div>
                {status.oauth.email && (
                  <div className="text-xs text-emerald-800 mt-0.5 font-mono">
                    {status.oauth.email}
                  </div>
                )}
                {status.oauth.connectedAt && (
                  <div className="text-xs text-emerald-700 mt-1">
                    {t('gtmConnect.connectedAt', {
                      date: new Date(status.oauth.connectedAt).toLocaleString(),
                    })}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 bg-white hover:bg-emerald-50 text-xs font-medium text-emerald-800 disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Unplug className="w-3 h-3" />
                )}
                {t('gtmConnect.disconnect')}
              </button>
            </div>
          </div>

          <div className="text-xs text-gray-600 space-y-1">
            <p>{t('gtmConnect.nextSteps')}</p>
            <a
              href="https://tagmanager.google.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
            >
              {t('gtmConnect.openGtm')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </Panel>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Local subcomponents — kept inline because they're only used here
//  and pulling them out would force a 3-file change for any visual
//  tweak.
// ────────────────────────────────────────────────────────────────────

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-6">{children}</div>
);

const Header: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}> = ({ icon, title, subtitle }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5">{icon}</div>
    <div>
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>
    </div>
  </div>
);
